// docs/44 MM-cache — the web build's sounds, kept like an installed game's. A while after the game has
// loaded, every sound the build ships (each bank's takes, the whole library, the music) is downloaded
// into the browser's Cache Storage, two at a time, streamed straight into the cache and paused while
// the tab is hidden; every sound the engine fetches reads the cache first and fills it as it goes. Only
// the bytes are kept: decoding stays on demand (decoded, the whole set would be ~17 GB). Once a sync
// has finished, files the build no longer ships are pruned.
//
// Production web builds only: their file names are content hashes, so a cached file can never go stale
// (the local dev server's are not). The desktop app reads its sounds from disk and skips all of this,
// as do e2e runs and a browser asking to save data.
import { isDesktop } from '../desktop';

const CACHE = 'fk-sfx';
/** Downloads at once: a sound the game is waiting for always has the rest of the pool. */
const PARALLEL = 2;
/** How long after the game loads the download starts (the first screen's art and sounds go first). */
const START_MS = 8000;

export interface DownloadState { total: number; done: number; failed: number; running: boolean; finished: boolean }
/** Progress, for `__fkSfx.info()` and anything that wants to show it. */
export const downloadState: DownloadState = { total: 0, done: 0, failed: 0, running: false, finished: false };

/** Whether this build keeps its sounds in Cache Storage. */
export function cacheEnabled(): boolean {
  const saveData = (globalThis.navigator as Navigator & { connection?: { saveData?: boolean } } | undefined)?.connection?.saveData;
  return typeof caches !== 'undefined' && import.meta.env.PROD && !__FK_E2E__ && !isDesktop() && !saveData;
}

const abs = (url: string) => new URL(url, globalThis.location?.href ?? 'http://localhost/').href;

let opened: Promise<Cache | undefined> | undefined;
const open = (): Promise<Cache | undefined> => (opened ??= caches.open(CACHE).catch(() => undefined));

/** A sound's bytes: from the cache if it is there, else the network (and then into the cache). */
export async function cachedBytes(url: string): Promise<ArrayBuffer | undefined> {
  const key = abs(url);
  const c = await open();
  try {
    const hit = await c?.match(key);
    if (hit) return await hit.arrayBuffer();
  } catch { /* a broken entry: fetch it again */ }
  const r = await fetch(key).catch(() => undefined);
  if (!r?.ok) return undefined;
  // a full quota just means this one plays from the network
  if (c) await c.put(key, r.clone()).catch(() => undefined);
  return r.arrayBuffer();
}

/** A local URL for a cached file (a music track streams from it), or undefined when it is not cached yet. */
export async function cachedObjectUrl(url: string): Promise<string | undefined> {
  try {
    const hit = await (await open())?.match(abs(url));
    return hit ? URL.createObjectURL(await hit.blob()) : undefined;
  } catch { return undefined; }
}

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

/** Starts the background download (once), `START_MS` after it is called. */
let started = false;
export function startDownload(urls: () => Promise<readonly string[]>): void {
  if (started || !cacheEnabled()) return;
  started = true;
  setTimeout(() => { void downloadAll(urls); }, START_MS);
}

/** Downloads every file of `urls` not yet cached, then prunes what the build no longer ships. */
export async function downloadAll(urls: () => Promise<readonly string[]>): Promise<void> {
  const c = await open();
  if (!c) return;
  // ask the browser to keep these rather than evict them under storage pressure (granted silently or not at all)
  await navigator.storage?.persist?.().catch(() => false);
  const all = [...new Set((await urls()).map(abs))];
  const have = new Set((await c.keys()).map((r) => r.url));
  const queue = all.filter((u) => !have.has(u));
  Object.assign(downloadState, { total: all.length, done: all.length - queue.length, failed: 0, running: true, finished: false });
  const worker = async () => {
    while (queue.length) {
      while (typeof document !== 'undefined' && document.hidden) await sleep(2000);
      const u = queue.shift()!;
      try {
        const r = await fetch(u);
        if (r.ok) { await c.put(u, r); downloadState.done++; } else downloadState.failed++;
      } catch { downloadState.failed++; }
    }
  };
  await Promise.all(Array.from({ length: PARALLEL }, worker));
  // a new build's files replace the last one's: drop what this build no longer ships
  if (!downloadState.failed) {
    const keep = new Set(all);
    for (const req of await c.keys()) if (!keep.has(req.url)) await c.delete(req).catch(() => false);
  }
  Object.assign(downloadState, { running: false, finished: downloadState.failed === 0 });
}
